/**
 * Created by rhinov25 on 31/01/2017.
 *
 * Management of decoration areas (addition, edition, deletion, selection)
 */
import { ActionState, ActionStateEnum, IdleConstruction } from "..";
import { EntityManager } from "../../plan/EntityManager";
import { AreaNode } from "../../plan/Nodes/AreaNode";
import { Command } from "../../../../constants";
import { PlanManager } from "../../../../cocosPlan";
import { Area, CoatingArea, Commands, ComponentConstants, Entity, Events, eventsManager, FloorCoatingArea, math, planManager, SavaneConstants, Segment } from "@rhinov/savane-js";

declare var cc: any;


// Edition mode enum
export const AreaStateEditModeEnum =
    {
        // No edit mode = leave this state on a click up
        unknown: 0,
        // Area selected mode, an area was clicked we display it as selected/highlighted
        area_selected: 1,
        // A point of an area is selected we can move the point on a mouse move
        point_selected: 2,
        // A line of an area is selected we can move this line on a mouse move
        line_selected: 3,
        // A new area is being created
        new_area: 4
    };

export class AreaState extends ActionState {

    _node: AreaNode | null;
    editMode: number;
    _transformMode: number;
    // No edited point yet
    _editedPointIndex: number | null = null;
    // No saved position for the edited point
    _savedPosition: math.vec3 | null = null;
    // No edited line yet
    _editedLineIndex: number | null = null;
    // No line direction yet
    _lineDirection: math.vec3 | null = null;
    // No saved positions for the line
    _savedPositions: math.vec3[] | null = null;
    // is editing
    _isEditing = false;
    // prevent colinear vertices
    _allowColinearVertices = false;
    // rotation center
    _rotationCenter: math.vec3 | null = null;
    _area: Area | FloorCoatingArea | CoatingArea | null = null;
    _entity: Entity | null = null;
    _dragging: boolean = false;
    _lastCursorPosition: math.vec3 | null = null;
    _isDrawingPoint: boolean = false;
    isAreaCreated: boolean = false;
    _previousPosition: math.vec3 | null = null;
    _angle: number = 0;
    _ctrlPressed: boolean = false;

    constructor(area: Area | null, position: any, coating?: any) {
        super();
        // Surrent state enum value from ActionState
        this.state = ActionStateEnum.DrawArea;
        // No edited node
        this._node = null;
        // For the moment we don't know the edit mode
        this.editMode = AreaStateEditModeEnum.unknown;
        // Transform mode
        this._transformMode = 0; // 0 - translation | 1 - rotation
        // Init the state variable and edit mdoe
        this.init(area, position, undefined, undefined, coating);
    }
     // Action state from a global enum
    getAction(): number {
        return ActionStateEnum.DrawArea;
    }

    // State variables initialisation depending on area potentially edited and cursor position
    init(area: Area | FloorCoatingArea | CoatingArea | null, cursorPosition: math.vec3 | null, entity?: Entity, doubleClick?: boolean, coating?: any) {
        

        // If no entity passed
        if (entity === undefined || entity === null) {
            // Edited entity is the current floor as all areas are attached to the floor itself
            this._entity = PlanManager.getInstance().world.currentScene!.currentFloor!;
        }
        else {
            // Otherwise attached to the passed entity
            this._entity = entity;
        }
        // Retrieve Coco node from entity
        this._node = EntityManager.getInstance().getNode(this._entity) as any;
        // Store edited area (if any, if no this will be null)
        this._area = area;
        // If the cocos node and area exist
        if (this._node !== null && this._area !== null) {
            // Retrieve the correct area component from the entity
            var components = this._entity.getComponents(ComponentConstants.ComponentType.Area);
            components = components.concat(this._entity.getComponents(ComponentConstants.ComponentType.FloorCoatingArea));
            for (var i = 0; i < components.length; i++) {
                // Found ?
                if (components[i] === area) {
                    // Yes, store the edited area in the cocos node member
                    this._node._currentEditedArea = i;
                    this._node.setLineSelected(null);
                    this._node.setPointSelected(null);
                    // Set the cocos node as selected
                    this._node.setSelected(true);
                    break;
                }
            }
        }

        // No position passed to the initialisation we aren't dragging yet
        if (cursorPosition === null) {
            this._dragging = false;
        }
        else {
            // Else we consider we are already dragging so we don't leave this state on the click up
            this._dragging = true;
        }
        // No cursor position yet
        this._lastCursorPosition = null;

        // If no area passed then it means this is a new area in the scene
        if (this._area === null) {
            // Create the area
            this._area = new Area();
            // Attach it to the entity (current floor)
            this._area.entity = this._entity;
            // No vertice yet in the area
            this._area.vertices = [];
            // We start drawing this area
            this._isDrawingPoint = true;
            this._isEditing = true;

            // The area isn't created yet, it will be at first mouse move or it will be invalid here
            this.isAreaCreated = false;

            // AreaState edition mode is create a new area
            this.editMode = AreaStateEditModeEnum.new_area;
        }
        else if (cursorPosition) {
            // We aren't drawing anything, we are editing an existing area
            this._isDrawingPoint = false;
            // No neet to create the area it already exist
            this.isAreaCreated = true

            if (doubleClick === undefined) {
                doubleClick = false;
            }

            // Let's see if we are editing a point or a line of the area by checking if the mouse cursor is on a point or a line_selected
            // This will fill _editedPointIndex, _savedPosition, _editedLineIndex and _savedPositions if it is the case
            // There is no double click here
            this.editMode = this.getAreaSelectionMode(cursorPosition, doubleClick)

            // If AreaState edition mode is still unknow then we are in area selected mode as default (will highlight the area)
            if (this.editMode === AreaStateEditModeEnum.unknown) {
                this.editMode = AreaStateEditModeEnum.area_selected;
            }
        }
    }

    // Check current area against the cursor position, points of the area and lines of the area to determine if we are editing a point or a line of it
    // We also take the double click to cut line segments and create a new points
    getAreaSelectionMode(cursorPosition: math.vec3, doubleClick: boolean): number {
        // Parse all vertices of the area
        var result = AreaStateEditModeEnum.unknown;
        this._editedLineIndex = null;
        this._editedPointIndex = null;
        var distance = Number.MAX_VALUE;
        var magnetDistance = 25;
        for (var i = 0; i < this._area!.vertices.length; i++) {
            // Get current vertice and next
            var current = this._area!.vertices[i];
            var next = this._area!.vertices[(i + 1) % this._area!.vertices.length];
            var segment = new Segment(math.vec3.fromValues(current[0], current[1], 0),
                math.vec3.fromValues(next[0], next[1], 0));

            // Double click ?
            if (doubleClick) {
                if (segment.isPointOnSegment(cursorPosition, magnetDistance)) {
                    // Add a point in the area with the cursor position
                    PlanManager.getInstance().executeCommand(this.editCommand([{
                        addedPoint: cursorPosition,
                        index: i + 1
                    }]));
                    this._editedPointIndex = i + 1;
                    // Set the cocos node as selected
                    if (this._node) {
                        this._node.setSelected(true);
                        this._node.setPointSelected(this._editedPointIndex);
                        this._node.setLineSelected(null as any);
                    }
                    // We are in area selected edition mode
                    return AreaStateEditModeEnum.point_selected;
                }
            }

            // Are we close from the 1st point of the segment ?
            var distanceToBegin = math.vec3.dist(segment.begin, cursorPosition);
            var distanceToEnd = math.vec3.dist(segment.end, cursorPosition);
            if (distanceToBegin < 2 * magnetDistance && distanceToBegin < distance) {
                distance = distanceToBegin;
                // Yes so we will edit this first point
                this._editedPointIndex = i;
                // Save its position for cancellation
                this._savedPosition = math.vec3.clone(this._area!.vertices[i]);
                // We are now in point_selected area state edition
                this._isEditing = true;
                if (this._node) {
                    this._node.setSelected(true);
                    this._node.setPointSelected(this._editedPointIndex);
                    this._node.setLineSelected(null as any);
                }
                result = AreaStateEditModeEnum.point_selected;
            }
            // Are we close from the 2nd point of the segment ?
            else if (distanceToEnd < 2 * magnetDistance && distanceToEnd < distance) {
                distance = distanceToEnd;
                // Yes so we will edit this first point
                this._editedPointIndex = i + 1;
                // Save its position for cancellation
                this._savedPosition = math.vec3.clone(this._area!.vertices[i + 1]);
                // We are now in point_selected area state edition
                this._isEditing = true;
                // Set the cocos node as selected
                if (this._node) {
                    this._node.setSelected(true);
                    this._node.setPointSelected(this._editedPointIndex);
                    this._node.setLineSelected(null as any);
                }
                result = AreaStateEditModeEnum.point_selected;
            }
        }

        if (result !== AreaStateEditModeEnum.unknown) {
            return result;
        }

        var distance = Number.MAX_VALUE;
        for (var i = 0; i < this._area!.vertices.length; i++) {
            // Get current vertice and next
            var current = this._area!.vertices[i];
            var next = this._area!.vertices[(i + 1) % this._area!.vertices.length];
            var segment = new Segment(math.vec3.fromValues(current[0], current[1], 0),
                math.vec3.fromValues(next[0], next[1], 0));

            // Are we on the segment itself (or close from)
            if (segment.isPointOnSegment(cursorPosition, magnetDistance)) {
                var project = segment.orthogonalProjection(cursorPosition)!;
                var distanceToSegment = math.vec3.dist(cursorPosition, project);
                if (distanceToSegment < distance) {
                    distance = distanceToSegment;
                    // No double click the segment line is selected
                    this._editedLineIndex = i;
                    // Save line position for cancellation
                    this._savedPosition = math.vec3.clone(cursorPosition);
                    this._savedPositions = [math.vec3.clone(segment.begin), math.vec3.clone(segment.end)];
                    // Get line direction for edition
                    this._lineDirection = math.vec3.clone(segment.direction);
                    // We are editing a line
                    this._isEditing = true;
                    // Set the cocos node as selected
                    if (this._node) {
                        this._node.setSelected(true);
                        this._node.setLineSelected(this._editedLineIndex);
                        this._node.setPointSelected(null as any);
                    }
                    result = AreaStateEditModeEnum.line_selected;
                }
            }
        }

        return result;
    }

    // Return the next state to go to (typically when this state ends)
    nextState(): ActionState | null {
        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.IdleConstruction);
        return new IdleConstruction()
    }

    // Create a command to delete the current area (so it can be undone)
    deleteCommand(): Command {
        return new Commands.DeleteComponentCommand(this._area!, false);
    }

    // Create a command to modify an area (so it can be undone)
    editCommand(param: any): Command {
        if (this._node) {
            this._node.setClosed(true);
        }
        return new Commands.EditComponentCommand(this._area!, param);
    }

    // Create a command to create a new area (so it can be undone)
    addCommand(): Command {
        var res = new Commands.AddComponentCommand(this._area!, this._entity!, false, false);
        if (res && this._node) {
            this._node.setClosed(true);
        }
        return res;
    }

    magnetPosition(index: number, position: math.vec3): math.vec3 {
        var result = math.vec3.clone(position);
        var snapped = false;
        // snap verticaly / horizontaly
        if (PlanManager.getInstance().snapEnabled && this._area!.vertices.length > 1) {
            var magnetDistance = 50;
            var pIndex = (index - 1 + this._area!.vertices.length) % this._area!.vertices.length;
            var nIndex = (index + 1) % this._area!.vertices.length;
            // x previous snap
            var previous = math.vec3.fromValues(this._area!.vertices[pIndex][0], this._area!.vertices[pIndex][1], 0);
            var next = math.vec3.fromValues(this._area!.vertices[nIndex][0], this._area!.vertices[nIndex][1], 0);
            var xSnapPosition = math.vec3.clone(previous);
            xSnapPosition[0] += 1;
            var segment = new Segment(previous, xSnapPosition);
            var project = segment.orthogonalProjection(result);
            if (project) {
                if (math.vec3.distance(result, project) < magnetDistance) {
                    result = math.vec3.clone(project);
                    snapped = true;
                }
            }

            // x next snap
            xSnapPosition = math.vec3.clone(next);
            xSnapPosition[0] += 1;
            segment = new Segment(next, xSnapPosition);
            project = segment.orthogonalProjection(result);
            if (project) {
                if (math.vec3.distance(result, project) < magnetDistance) {
                    result = math.vec3.clone(project);
                    snapped = true;
                }
            }

            // y previous snap
            var ySnapPosition = math.vec3.clone(previous);
            ySnapPosition[1] += 1;
            segment = new Segment(previous, ySnapPosition);
            project = segment.orthogonalProjection(result);
            if (project) {
                if (math.vec3.distance(result, project) < magnetDistance) {
                    result = math.vec3.clone(project);
                    snapped = true;
                }
            }

            // y next snap
            ySnapPosition = math.vec3.clone(next);
            ySnapPosition[1] += 1;
            segment = new Segment(next, ySnapPosition);
            project = segment.orthogonalProjection(result);
            if (project) {
                if (math.vec3.distance(result, project) < magnetDistance) {
                    result = math.vec3.clone(project);
                    snapped = true;
                }
            }
        }
        if (snapped) {
            return result;
        } else {
            return position;
        }
    }

    // Edit a point of an area and broadcast a change was done
    editPoint(index: number, position: math.vec3, nosnap: boolean | undefined): math.vec3 | void {
        if (PlanManager.getInstance().snapEnabled && !nosnap) {
            position = this.magnetPosition(index, position);
        }
        // Assign vertice new position into array
        this._area!.vertices[index] = position;
        return position;
    }

    // Called when the cursor button is pressed
    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number, removeArea?: boolean): ActionState | null {
        if (mouseButton === 2) {
            return PlanManager.getInstance().mouseClickCancelled();
        }
        // If we don't have any cursor position yet, store it so we can detect draggings
        this._dragging = false;
        if (this._lastCursorPosition === null) {
            this._lastCursorPosition = math.vec3.clone(cursorPosition);
        }

        var magnetDistance = 25;
        this._previousPosition = math.vec3.clone(cursorPosition);
        this._angle = 0;
        // Are we drawing points ?
        if (this._isDrawingPoint) {
            // More than 2 points and we are clicking on the first area point ==> We are closing and creating the area
            if (this._area!.vertices.length > 3 && math.vec3.dist(cursorPosition, this._area!.vertices[0]) < 2 * magnetDistance) {
                // We aren't drawing anymore now, the area is closed
                this._isDrawingPoint = false;
                // We remove the latest point added (in order not to have a duplicate point at the start and end of the vertice array)
                this._area!.vertices.splice(this._area!.vertices.length - 1, 1);

                // If the area is valid
                if (this._isValid()) {
                    // We remove the area
                    if (removeArea && this._node) {
                        this._node.removeArea();
                    }
                    // We create it nicely with a command so it can be undone
                    PlanManager.getInstance().executeCommand(this.addCommand());
                    // We reinit current state for next area to create
                    this.init(null, null, undefined, undefined);
                }
                else {
                    // We do not accept this area since it is invalid so continue edition
                    this._isDrawingPoint = true;
                    this._isEditing = true;
                    // We push the latest point again so it can be edited correctly
                    this._area!.vertices.push(math.vec3.clone(this._area!.vertices[0]));
                }
            }
            else {
                // Less than 3 points, accept the point only of it creates a valid area
                if (this._isValid() && (this._area!.vertices.length <= 1 || math.vec3.dist(cursorPosition, this._area!.vertices[0]) > magnetDistance)) {
                    this._area!.vertices.push(math.vec3.clone(cursorPosition));
                }
            }
        }
        else {
            // Are we just showing the selected area for the moment (click on an area but no point or line of it)
            if (this.editMode !== AreaStateEditModeEnum.unknown) {
                // Yes, try to see if we are clicking on a point or line of this area
                this.editMode = this.getAreaSelectionMode(cursorPosition, doubleClick);

                // No point or line ?
                if (this._editedPointIndex === null && this._editedLineIndex === null) {
                    // Check if a new area is clicked now
                    var area = planManager.getAreaAtPosition(cursorPosition, [
                        ComponentConstants.ComponentType.Area,
                        ComponentConstants.ComponentType.FloorCoatingArea,
                    ], true, PlanManager.getInstance().world.currentScene!.currentFloor!);
                    // If yes and a new area is clicked
                    if (area !== null && area !== this._area) {
                        // We are in draw area plan state again
                        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.DrawArea);
                        // Unselect current node (so new one can be selected)
                        if (this._node) {
                            this._node.setSelected(false);
                        }
                        // Reinit the current state with new area and cursor position
                        this.init(area, cursorPosition, undefined, undefined);
                    }
                    if (area === this._area) {
                        this.editMode = AreaStateEditModeEnum.area_selected;
                    }
                }
            } else {
                this.editMode = this.getAreaSelectionMode(cursorPosition, doubleClick);
                if (this.editMode === AreaStateEditModeEnum.unknown) {
                    this.cancelAllActions();
                }
            }
        }

        // We do not leave the current state on click down
        return null;
    }

    // Called when the mouse button is released
    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        // Are we editing a point ?
        if (this._editedPointIndex !== null && this._savedPosition !== null) {
            // If the area isn't valid
            if (!this._isValid()) {
                // Put back the original point coordinate
                this.editPoint(this._editedPointIndex, this._savedPosition, undefined);
            }
            else {
                // Create a command to edit the point and make it move to the new position
                var begin = this._savedPosition;
                var end = math.vec3.clone(this._area!.vertices[this._editedPointIndex]);
                this.editPoint(this._editedPointIndex, this._savedPosition, undefined);
                PlanManager.getInstance().executeCommand(this.editCommand([{
                    begin: begin,
                    end: end,
                    index: this._editedPointIndex
                }]));
            }

            // Edition has ended let's go back to area selected state
            this._savedPosition = null;
        }
        else {
            var magnetDistance = 25;
            // Are we editing a line ?
            if (this._editedLineIndex !== null && this._savedPosition !== null && this._savedPositions !== null) {
                // Only accept a significant move
                if (math.vec3.dist(this._savedPosition, cursorPosition) > magnetDistance) {
                    // If the area isn't valid
                    if (!this._isValid()) {
                        // Put back the points at their original positions
                        this.editPoint(this._editedLineIndex, this._savedPositions[0], true);
                        this.editPoint((this._editedLineIndex + 1) % this._area!.vertices.length, this._savedPositions[1], true);
                    }
                    else {
                        // Create a command to move both points to the final destinations
                        var editParams = [
                            {
                                begin: this._savedPositions[0],
                                end: math.vec3.clone(this._area!.vertices[this._editedLineIndex]),
                                index: this._editedLineIndex
                            },
                            {
                                begin: this._savedPositions[1],
                                end: math.vec3.clone(this._area!.vertices[(this._editedLineIndex + 1) % this._area!.vertices.length]),
                                index: (this._editedLineIndex + 1) % this._area!.vertices.length
                            }
                        ];

                        this.editPoint(this._editedLineIndex, this._savedPositions[0], true);
                        this.editPoint((this._editedLineIndex + 1) % this._area!.vertices.length, this._savedPositions[1], true);
                        PlanManager.getInstance().executeCommand(this.editCommand(editParams));
                    }
                }
                else if (this._dragging) {
                    // Put back the points at their original positions
                    this.editPoint(this._editedLineIndex, this._savedPositions[0], true);
                    this.editPoint((this._editedLineIndex + 1) % this._area!.vertices.length, this._savedPositions[1], true);
                }

                // Edition has ended let's go back to area selected state
                this._savedPositions = null;
                this._savedPosition = null;
            }
            else {
                if (this._dragging && this._savedPositions) {
                    // Put back the points at their original positions
                    if (!this._isValid()) {
                        for (var i = 0; i < this._area!.vertices.length; ++i) {
                            this.editPoint(i, this._savedPositions[i], true);
                        }
                    }
                    else {
                        editParams = [];
                        for (var i = 0; i < this._area!.vertices.length; ++i) {
                            editParams.push({ begin: this._savedPositions[i],
                                                end: math.vec3.clone(this._area!.vertices[i]),
                                                index: i});
                        }
                        for (var i = 0; i < this._area!.vertices.length; ++i) {
                            this.editPoint(i, this._savedPositions[i], true);
                        }
                        PlanManager.getInstance().executeCommand(this.editCommand(editParams));
                    }
                    this._savedPositions = null;
                }
            }
        }

        this._dragging = false;
        this._lastCursorPosition = null;
        this._rotationCenter = null;
        this._previousPosition = null;
        this._angle = 0;
        // Depending on edition mode
        switch (this.editMode) {
            // If unknow edition mode let's go back to IdleConstruction
            case AreaStateEditModeEnum.unknown:
                return this.nextState();

            // Other cases, we stay in the current state
            default:
                return (null);
        }
    }

    _moveMouseCursorTranslateMode(cursorPosition: math.vec3): ActionState | null {
        // If the cursor has moved
        if ((this._lastCursorPosition !== null) && (math.vec3.distance(this._lastCursorPosition, cursorPosition) > SavaneConstants.PositionTolerance)) {
            // The mouse is dragged
            this._dragging = true;
            this._isEditing = true;
        }

        if (this._previousPosition && this._lastCursorPosition || this._isDrawingPoint || this._dragging) {
            // Clone the cursor position for further storage
            cursorPosition = math.vec3.clone(cursorPosition);
            // Create vertice array at first mouse movement
            if (!this.isAreaCreated && this._node !== null) {
                // Do not create it again next time
                this.isAreaCreated = true;
                // Store the first area vertex
                this._area!.vertices = [cursorPosition];
                // Create the area and attach it to the entity
                this._node.createArea(this._area!);
                this._node.setClosed(false);
                this._node.setSelected(true);
            }

            // If the area exists
            if (this._area !== null) {
                // If we are editing a point
                if (this._editedPointIndex !== null) {
                    // Move the point to the current cursor position
                    this.editPoint(this._editedPointIndex, cursorPosition, undefined);
                }
                else {
                    // If we are editing a line of the area
                    if (this._editedLineIndex !== null) {
                        if (this._previousPosition) {
                            var delta = math.vec3.create();
                            math.vec3.subtract(delta, cursorPosition, this._previousPosition);

                            var p1 = math.vec3.clone(this._area!.vertices[this._editedLineIndex]);
                            math.vec3.add(p1, p1, delta);

                            var p2 = math.vec3.clone(this._area!.vertices[(this._editedLineIndex + 1) % this._area!.vertices.length]);
                            math.vec3.add(p2, p2, delta);

                            // Move the two points to their final destinations
                            this.editPoint(this._editedLineIndex, p1, true);
                            this.editPoint((this._editedLineIndex + 1) % this._area!.vertices.length, p2, true);
                        }
                    }
                    else {
                        // If we are drawing an area
                        if (this._isDrawingPoint) {
                            // Move current point to the correct position
                            this.editPoint(this._area!.vertices.length - 1, cursorPosition, undefined);
                        } else if (this._dragging && this._previousPosition) {
                            if (!this._savedPositions) {
                                this._savedPositions = [];
                                for (var i = 0; i < this._area!.vertices.length; ++i) {
                                    this._savedPositions.push(this._area!.vertices[i]);
                                }
                            }

                            var delta = math.vec3.create();
                            math.vec3.subtract(delta, cursorPosition, this._previousPosition);
                            for (var i = 0; i < this._area!.vertices.length; ++i) {
                                var dest = math.vec3.create();
                                math.vec3.add(dest, this._area!.vertices[i], delta);
                                this.editPoint(i, dest, true);
                            }
                        }
                    }
                }
            }
        }

        this._previousPosition = math.vec3.clone(cursorPosition);
        return null;
    }

    _moveMouseCursorRotateMode(cursorPosition: math.vec3): ActionState | null {
        if (this._previousPosition) {
            cursorPosition = math.vec3.clone(cursorPosition);
            if (this._rotationCenter === null) {
                var aabb = (this._area as Area).aabb;
                this._rotationCenter = math.vec3.create();
                math.vec3.add(this._rotationCenter, aabb.min, aabb.max);
                math.vec3.divide(this._rotationCenter, this._rotationCenter, math.vec3.fromValues(2, 2, 2));
            }
            var d1 = math.vec3.create();
            math.vec3.subtract(d1, this._previousPosition, this._rotationCenter);
            math.vec3.normalize(d1, d1);
            var d2 = math.vec3.create();
            math.vec3.subtract(d2, cursorPosition, this._rotationCenter);
            math.vec3.normalize(d2, d2);

            var up = math.vec3.create();
            math.vec3.cross(up, d1, d2);

            var angle = Math.acos(math.vec3.dot(d1, d2));

            if (isFinite(angle) === false) return null;
            this._angle += angle;

            if (!this._savedPositions) {
                this._savedPositions = [];
                for (var i = 0; i < this._area!.vertices.length; ++i) {
                    this._savedPositions.push(this._area!.vertices[i]);
                }
            }

            if (this._ctrlPressed) {
                //Snap rotation every 45°
                if (Math.abs(this._angle) >= Math.PI / 4) {
                    angle = Math.PI / 4;
                    angle *= (this._angle < 0 ? -1 : 1);
                    this._angle = 0;
                } else {
                    angle = 0;
                }
            }

            var rotation = math.mat4.create();
            math.mat4.fromRotation(rotation, angle, up);
            for (var i = 0; i < this._area!.vertices.length; ++i) {
                var dest = math.vec3.create();
                math.vec3.subtract(dest, this._area!.vertices[i], this._rotationCenter!);
                math.vec3.transformMat4(dest, dest, rotation);
                math.vec3.add(dest, dest, this._rotationCenter!);
                this.editPoint(i, dest, true);
            }
        }

        this._previousPosition = math.vec3.clone(cursorPosition);
        return null;
    }

    // Called when the mouse moves in this state
    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        super.moveMouseCursor(cursorPosition);
        eventsManager.instance.dispatch(Events.STOP_UPDATING_ENVS);
        switch (this._transformMode) {
            case 0:
                return this._moveMouseCursorTranslateMode(cursorPosition);
            case 1:
                return this._moveMouseCursorRotateMode(cursorPosition);
        }
        return null;
    }

    // Check if the edited area is valid
    _isValid(): boolean {
        // Edited area vertices
        var vertices = this._area!.vertices;

        // If there is at least one point in the list
        if (vertices.length > 0) {
            // If we are still drawing points and less than 2 vertices available we consider the area as valid
            if (this._area!.vertices.length < 2 && this._isDrawingPoint) {
                return true;
            } else if (this._area!.vertices.length > 2) {
                // bypass colinear check
                if (this._allowColinearVertices) return true;
                // Prevent colinear vertices
                for (var i = 0; i < this._area!.vertices.length; ++i) {
                    var current = this._area!.vertices[i];
                    var next = this._area!.vertices[(i + 1) % this._area!.vertices.length];
                    var second = this._area!.vertices[(i + 2) % this._area!.vertices.length];
                    var segment = new Segment(current, second);
                    if (segment.isPointOnSegment(next, 0.01) === true) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    // Get first vertex of the area (used externally)
    getFirstVertex(): math.vec3 | null {
        if (this._isDrawingPoint && this._area !== null && this._area.vertices.length > 1) {
            return math.vec3.clone(this._area!.vertices[0]);
        }
        return null;
    }

    // Returns the first point of the last edited segment (used externally)
    get beginPoint(): math.vec3 | null {
        if (this._isDrawingPoint && this._area !== null && this._area.vertices.length > 2) {
            return math.vec3.clone(this._area!.vertices[this._area!.vertices.length - 2]);
        }
        return null;
    }

    // Returns last real segment of the area (used externally)
    get lastSegment(): Segment | null {
        if (this._isDrawingPoint && this._area !== null && this._area.vertices.length > 2) {
            return new Segment(this._area!.vertices[this._area!.vertices.length - 2], this._area!.vertices[this._area!.vertices.length - 3]);
        }
        return null;
    }

    // Are we editing something at the moment (used externally)
    isEditing(): boolean {
        return this._isEditing;
    }

    // Cancel all edition action potentially in progress
    cancelAllActions() {
        // Draw new area
        if (this._isDrawingPoint) {
            this._area!.vertices = [];
            eventsManager.instance.dispatch(Events.COMPONENT_REMOVED, this._area!);
        }

        // Edit point of the area
        if (this._editedPointIndex !== null && this._savedPosition) {
            // Put back the point at its original location
            this.editPoint(this._editedPointIndex, this._savedPosition, undefined);
            // We are no more editing now
            this._savedPosition = null;
            this._editedPointIndex = null;
        }

        // Edit line of the area
        if (this._editedLineIndex !== null && this._savedPositions) {
            // Put back the line 2 points at their original location
            this.editPoint(this._editedLineIndex, this._savedPositions[0], true);
            this.editPoint((this._editedLineIndex + 1) % this._area!.vertices.length, this._savedPositions[1], true);
            // We are no more editing now
            this._savedPositions = null;
            this._savedPosition = null;
            this._editedLineIndex = null;
        }

        if (this._dragging && this._savedPositions) {
            for (var i = 0; i < this._area!.vertices.length; ++i) {
                this.editPoint(i, this._savedPositions[i], true);
            }
            this._savedPositions = null;
            this._savedPosition = null;
        }
        this._dragging = false;
        this._isEditing = false;
        this._isDrawingPoint = false;
        this.editMode = AreaStateEditModeEnum.unknown;
    }

    // Called when a key is pressed
    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        // Delete and backspace
        if ((keyCode === cc.KEY.Delete || keyCode == cc.KEY.backspace) && this._area !== null) {
            var wasValid = this._isValid();
            if (this._editedPointIndex !== null && this._area!.vertices.length > 3) {
                var removed = this._area!.vertices.splice(this._editedPointIndex, 1);
                if (!this._isValid() && wasValid === true) {
                    this._area!.vertices.splice(this._editedPointIndex, 0, removed[0]);
                }
                this._editedPointIndex = null;
            } else if (this._editedLineIndex !== null && this._area!.vertices.length > 4) {
                var removed = this._area!.vertices.splice(this._editedLineIndex, 2);
                if (!this._isValid() && wasValid === true) {
                    this._area!.vertices.splice(this._editedLineIndex, 0, removed[0]);
                    this._area!.vertices.splice(this._editedLineIndex + 1, 0, removed[1]);
                }
                this._editedLineIndex = null;
            } else if (this._area!.entity !== null && !this._isDrawingPoint) {
                // Delete current area using a command
                var entity = this._area!.entity;
                PlanManager.getInstance().executeCommand(this.deleteCommand());
                this._entity = entity;
            }

            // GO back to IdleConsruction
            return this.nextState();
        }

        // + key
        if (keyCode === 223 || keyCode === 107) {
            this._expand();
        }

        // - key
        if (keyCode === 187 || keyCode === 109) {
            this._shrink();
        }

        if (keyCode === cc.KEY.ctrl) {
            this._ctrlPressed = true;
        }

        // Escape, we cancel current action
        if (keyCode === cc.KEY.escape) {
            this.cancelAllActions();

            return this.nextState();
        }

        return null;
    }

    keyUnPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        if (keyCode === cc.KEY.ctrl) {
            this._ctrlPressed = false;
        }

        return null;
    }

    _expand() {
        if (this._area!.vertices.length < 3) return;
        var barycenter = this._barycenter();
        for (var i = 0; i < this._area!.vertices.length; ++i) {
            var direction = math.vec3.create();
            math.vec3.subtract(direction, this._area!.vertices[i], barycenter);
            math.vec3.multiply(direction, direction, math.vec3.fromValues(0.01, 0.01, 0.01));
            math.vec3.add(this._area!.vertices[i], this._area!.vertices[i], direction);
        }
    }

    _shrink() {
        if (this._area!.vertices.length < 3) return;
        var barycenter = this._barycenter();
        for (var i = 0; i < this._area!.vertices.length; ++i) {
            var direction = math.vec3.create();
            math.vec3.subtract(direction, barycenter, this._area!.vertices[i]);
            math.vec3.multiply(direction, direction, math.vec3.fromValues(0.01, 0.01, 0.01));
            math.vec3.add(this._area!.vertices[i], this._area!.vertices[i], direction);
        }
    }

    _barycenter(): math.vec3 {
        var result = math.vec3.create();
        for (var i = 0; i < this._area!.vertices.length; ++i) {
            math.vec3.add(result, result, this._area!.vertices[i]);
        }
        var divider = this._area!.vertices.length;
        math.vec3.divide(result, result, math.vec3.fromValues(divider, divider, divider));
        return result;
    }

    // Right click management, we cancel any edition action
    mouseClickCancelled(cursorPosition: math.vec3): ActionState | null {
        this.cancelAllActions();
        
        // And we go back to IdleConstruction state
        return this.nextState();
    }

    // Object destruction, we unselect the graphical node
    dispose() {
        if (this._node !== null) {
            this._node.setSelected(false);
        }
    }
}